import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Lock, Star, Check, Hourglass, Dices } from "lucide-react";
import { PathMap } from "../components/PathMap";
import { StudentShell } from "../components/StudentShell";
import { api, useAuth } from "../api";
import { gradeProfile, gameDiff } from "../gradeProfile";
import { sfx } from "../sfx";
import { TaskRunner, TASK_META, effectiveTask } from "../components/tasks";

const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${String(Math.floor(s / 3600)).padStart(2, "0")}:${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const Rest = ({ until, restHours }) => {
  const [left, setLeft] = useState(new Date(until) - new Date());
  useEffect(() => {
    const id = setInterval(() => setLeft(new Date(until) - new Date()), 1000);
    return () => clearInterval(id);
  }, [until]);
  return (
    <div className="play-card text-center max-w-lg mx-auto mt-10" data-testid="rest-gate">
      <Hourglass size={40} className="mx-auto" style={{ color: "var(--play-accent)" }} />
      <h2 className="text-2xl font-black mt-4">وقت الاستراحة 🌙</h2>
      <p className="text-white/70 mt-2 text-sm leading-loose">
        أكملت ٣ مراحل، أحسنت! تعود المراحل بعد {restHours} ساعة حتى يرتاح عقلك وتتذكّر الكلمات أفضل.</p>
      <div className="text-4xl font-black mt-6 tabular-nums" dir="ltr" data-testid="rest-countdown">
        {fmt(left)}</div>
      {left <= 0 && (
        <button onClick={() => window.location.reload()} data-testid="rest-refresh-btn"
          className="play-btn text-white mt-6" style={{ background: "var(--play-primary)" }}>
          تابع الرحلة</button>
      )}
    </div>
  );
};

const Roulette = ({ options, chosen, onReady }) => {
  const [i, setI] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      setI(n % options.length);
      if (n > 12) {
        clearInterval(id);
        if (!done.current) { done.current = true; sfx.points?.(); setTimeout(onReady, 700); }
      }
    }, 140);
    return () => clearInterval(id);
  }, []);
  const key = done.current ? chosen : options[i];
  const meta = TASK_META[key] || {};
  return (
    <div className="play-card text-center max-w-md mx-auto" data-testid="roulette">
      <Dices size={28} className="mx-auto text-white/60" />
      <p className="text-sm text-white/70 mt-2">القرعة تختار مهمتك...</p>
      <motion.div key={key} initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="mt-6 rounded-3xl py-8 text-white" style={{ background: meta.color }}>
        <div className="text-5xl">{meta.emoji}</div>
        <div className="text-xl font-black mt-3">{meta.label}</div>
      </motion.div>
    </div>
  );
};

export default function Journey() {
  const { user } = useAuth();
  const profile = gradeProfile(user?.grade);
  const [map, setMap] = useState(null);
  const [spin, setSpin] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/journey/map").then((r) => setMap(r.data));
  useEffect(() => { load(); }, []);

  const start = async (index) => {
    setBusy(true);
    try {
      const { data } = await api.post("/journey/spin", { index });
      setSpin(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر بدء المرحلة");
    } finally { setBusy(false); }
  };

  const finish = async (correct, total) => {
    const { index } = spin.stage;
    setPlaying(null);
    try {
      const { data } = await api.post("/journey/complete", { index, task: spin.task, correct, total });
      sfx.points?.();
      setResult(data);
      setSpin(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر حفظ النتيجة");
    }
  };

  const stages = map?.stages || [];
  const currentStage = useMemo(() => stages.find((s) => s.status === "current"), [stages]);

  if (!map) {
    return <StudentShell><p className="text-white/50">جارٍ تحميل الخريطة...</p></StudentShell>;
  }

  if (result) {
    const chest = result.chest;
    return (
      <StudentShell>
        <div className="play-card text-center max-w-md mx-auto mt-10" data-testid="stage-result">
          {chest ? (
            <div data-testid="chest-reward">
              <motion.div initial={{ scale: 0.4, rotate: -12, opacity: 0 }}
                animate={{ scale: [0.4, 1.15, 1], rotate: [-12, 6, 0], opacity: 1 }}
                transition={{ duration: 0.8 }} className="text-7xl">{chest.emoji}</motion.div>
              <h2 className="text-2xl font-black mt-4">فتحت {chest.name}!</h2>
              <p className="text-white/75 mt-2 text-sm">
                أكملت {chest.level * (map.chest_every || 10)} محطة — شارة جديدة:
                <b className="text-[#ffb43f]"> {chest.badge}</b></p>
              <p className="text-white/70 mt-1 text-sm">+{chest.bonus} نقطة مكافأة 🎁</p>
              {chest.certificate && (
                <button onClick={() => window.print()} data-testid="chest-certificate-btn"
                  className="pro-btn bg-white/12 text-white border border-white/20 text-xs mt-4">
                  🖨️ اطبع شهادتك</button>
              )}
              <div className="border-t border-white/10 mt-5 pt-4 text-sm text-white/70">
                نجوم المرحلة: {"⭐".repeat(result.stars)} · مجموع نقاطك: {result.total_points}</div>
            </div>
          ) : (
            <>
              <div className="flex justify-center gap-2">
                {[1, 2, 3].map((n) => (
                  <Star key={n} size={40} fill={n <= result.stars ? "#ffb43f" : "transparent"}
                    style={{ color: "#ffb43f" }} />
                ))}
              </div>
              <h2 className="text-2xl font-black mt-4">أحسنت! +{result.points_earned} نقطة</h2>
              <p className="text-white/70 mt-2 text-sm">مجموع نقاطك: {result.total_points}</p>
            </>
          )}
          <button onClick={() => { setResult(null); load(); }} data-testid="result-continue-btn"
            className="play-btn text-white mt-6 w-full" style={{ background: "var(--play-primary)" }}>
            {result.resting_until ? "إلى الاستراحة" : "المرحلة التالية"}</button>
        </div>
      </StudentShell>
    );
  }

  if (map.resting_until) {
    return <StudentShell><Rest until={map.resting_until} restHours={map.rest_hours} /></StudentShell>;
  }

  if (spin && !playing) {
    return (
      <StudentShell>
        <Roulette options={spin.options} chosen={spin.task} onReady={() => setPlaying(true)} />
      </StudentShell>
    );
  }

  if (spin && playing) {
    const shown = effectiveTask(spin.task, spin.payload);
    const meta = TASK_META[shown] || {};
    return (
      <StudentShell>
        <div className="play-card max-w-2xl mx-auto" data-testid="task-area" style={{ borderColor: meta.color }}>
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-xs text-white/60">المرحلة {spin.stage.index}
            {gameDiff(user?.grade, spin.stage.index).level > 0
              && ` · صعوبة +${gameDiff(user?.grade, spin.stage.index).level}`}</div>
              <h2 className="text-xl font-black">{meta.emoji} {meta.label}</h2>
            </div>
            <button onClick={() => { setSpin(null); setPlaying(null); }} data-testid="task-exit-btn"
              className="pro-btn bg-white/12 text-white border border-white/20 text-xs">خروج</button>
          </div>
          <p className="text-xs text-white/50 mb-4">{spin.stage.topic_title_ar}</p>
          <TaskRunner task={spin.task} payload={spin.payload} grade={user?.grade}
            stage={spin.stage?.index} onDone={finish} />
        </div>
      </StudentShell>
    );
  }

  return (
    <StudentShell>
      <h1 className="text-4xl font-black text-white glow-text">خريطة الرحلة</h1>
      <p className="text-white/70 mt-2 text-sm">
        {profile.label.ar} · مرحلة {map.current} من {stages.length} · ⭐ {map.points} نقطة ·
        استراحة {map.rest_hours} ساعة بعد كل {map.rest_after} مراحل</p>

      {currentStage && (
        <div className="play-card mt-6 flex flex-wrap items-center gap-4" data-testid="current-stage-card">
          <span className="h-14 w-14 rounded-full grid place-items-center text-xl font-black text-white"
            style={{ background: "var(--play-accent)" }}>{currentStage.index}</span>
          <div className="flex-1 min-w-[12rem]">
            <div className="font-black">{currentStage.topic_title_ar}</div>
            <div className="text-xs text-white/60" dir="ltr">{currentStage.topic_title_en}</div>
          </div>
          <button onClick={() => start(currentStage.index)} disabled={busy} data-testid="start-stage-btn"
            className="play-btn text-white disabled:opacity-50" style={{ background: "var(--play-primary)" }}>
            {busy ? "..." : "ابدأ المرحلة"}</button>
        </div>
      )}

      <div className="mt-2" data-testid="journey-path">
        <PathMap stages={stages} restAfter={map.rest_after} chestEvery={map.chest_every || 10}
          onStart={start}
          cols={typeof window !== "undefined" && window.innerWidth < 640 ? 3 : 4} />
      </div>
    </StudentShell>
  );
}
